import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchInput from '../shared/SearchInput';
import HeaderComponent from '../HeaderComponent';
import CalendarComponent from '../CalendarComponent';
import OpenNavigation from '../OpenNavigation';
import { Toast, LoadingState, EmptyState } from '../shared';
import useFilterData from '../hooks/useFilterData';




const FilterTemplate = ({
  title,
  subtitle = title,
  helpButton = null,
  backRoute = '/profile',
  searchPlaceholder = 'Search...',
  stylesheetHref,
  fetchData,
  dataKey,
  searchFields = [],
  typingSearchFields = null,
  renderCard,
  renderModal,
  onSubmit,
  emptyIcon = '📋',
  emptyTitle = 'No Results Found',
  loadingMessage = 'Loading...',
  showNavigation = true,
  showHeader = true,
  chipLogic = 'and',
  renderChips = [],
  currentMonthFilter,
  extraDataKeys = [],
  todayFilterIndex = null,
  onDownload = null,
  // ── Optional callbacks for parents that need to react to filter state ──
  onChipQueryChange = null,   // (chipQuery: string)        => void
  onTypedQueryChange = null,  // (typedQuery: string)       => void   ← NEW
  onSetTypedQueryRef = null,  // ref -> ref.current = setTypedQuery   ← NEW
  onFilteredChange  = null,
  onItemsChange     = null,   // (items: any[])             => void  (full unfiltered dataset)
  renderExtraActions = null,  // ()                          => ReactNode  (slot inside fr-results-actions)
  // ── Imperative refetch handle ──
  // Parent passes a ref; we attach a function to ref.current that, when
  // called, re-runs fetchData for the currently-selected month. Useful
  // after a parent-triggered mutation (e.g. delete) so the list reflects
  // server state without remounting the component.
  onRefetchRef = null,
}) => {
  const navigate = useNavigate();

  const {
    filteredItems,
    items,
    totalCount,
    loading,

    // typed input
    typedQuery,
    setTypedQuery,

    // chip selections
    chipQuery,
    setChipQuery,

    hasActiveFilters,

    isOpenNavigation,
    selectedMonthYear,
    handleMonthYearSelect,
    handleNavigationToOpen,
    handleNavigationToClose,
    selectedItem,
    isSubmitting,
    handleCardClick,
    handleModalClose,
    handleSubmit,
    toast,
    setToast,
    getFilterTitle,
    extraData,
    chipsOpen,
    setChipsOpen,
    getData,  // ← surfaced from useFilterData
  } = useFilterData({
    fetchData,
    dataKey,
    searchFields,
    typingSearchFields,
    currentMonthFilter,
    stylesheetHref,
    chipLogic,
    extraDataKeys,
    todayFilterIndex,
  });

  // ── Expose a refetch handle to the parent via ref ──
  // Parent calls onRefetchRef.current() to trigger a refetch of the
  // current month's data. Returns a promise that resolves when getData
  // completes, so the parent can sequence post-refetch UI (e.g. show a
  // success toast only after the fresh list has rendered).
  useEffect(() => {
    if (!onRefetchRef) return;
    onRefetchRef.current = async () => {
      if (selectedMonthYear) await getData(selectedMonthYear, false);
    };
    return () => {
      if (onRefetchRef) onRefetchRef.current = null;
    };
  }, [onRefetchRef, getData, selectedMonthYear]);

  // ── Notify parent when chipQuery changes ──
  useEffect(() => {
    if (onChipQueryChange) onChipQueryChange(chipQuery);
  }, [chipQuery, onChipQueryChange]);

  // ── Notify parent when the unfiltered dataset changes ──
  useEffect(() => {
    if (onItemsChange) onItemsChange(items);
  }, [items, onItemsChange]);

  // ── Notify parent when the filtered list changes ──
  useEffect(() => {
    if (onFilteredChange) onFilteredChange(filteredItems);
  }, [filteredItems, onFilteredChange]);

  // ── Notify parent when the typed search changes ──
  useEffect(() => {
    if (onTypedQueryChange) onTypedQueryChange(typedQuery);
  }, [typedQuery, onTypedQueryChange]);

  // ── Let the parent set the typed search (autocomplete pill click) ──
  useEffect(() => {
    if (!onSetTypedQueryRef) return;
    onSetTypedQueryRef.current = setTypedQuery;
    return () => {
      if (onSetTypedQueryRef) onSetTypedQueryRef.current = null;
    };
  }, [onSetTypedQueryRef, setTypedQuery]);

  const handleFormSubmit = (data) => {
    handleSubmit(onSubmit, data);
  };

  return (
    <div className="mobile-training-container pt-4">
      {showHeader && (
        <HeaderComponent
          title={title}
          backRoute={backRoute}
          backLabel="Back"
          searchValue={typedQuery}
          onSearchChange={setTypedQuery}
          searchPlaceholder={searchPlaceholder}
          onBack={() => navigate(backRoute)}
        />
      )}

      {!showHeader && (
        <SearchInput
          value={typedQuery}
          onChange={setTypedQuery}
          placeholder={searchPlaceholder}
        />
      )}

      {showNavigation && (
        isOpenNavigation ? (
          <div className="fr-calendar-wrapper">
            <CalendarComponent
              onMonthYearSelect={handleMonthYearSelect}
              onClose={handleNavigationToClose}
            />
          </div>
        ) : (
          <OpenNavigation onOpenNavButton={handleNavigationToOpen} />
        )
      )}

      <div className="fr-results-bar">
        <div className="fr-results-info">
          <span className="fr-results-title">{subtitle}</span>
          <span className="fr-results-count">
            Total: {totalCount} &nbsp; Filtered: {getFilterTitle()}
          </span>
        </div>
        <div className="fr-results-actions">
          {hasActiveFilters && (
            <span className="fr-results-search">
              Showing {filteredItems.length} of {items.length}
            </span>
          )}
          {helpButton}
          {onDownload && (
            <button
              className="fr-download-btn"
              onClick={() => onDownload(selectedMonthYear)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          )}
          {renderExtraActions && renderExtraActions({ selectedMonthYear, chipQuery, setToast, getData })}
        </div>
      </div>

      {renderChips && renderChips(chipQuery, setChipQuery, chipsOpen, setChipsOpen)}

      <div className="fr-cards-container">
        {loading ? (
          <LoadingState message={loadingMessage} />
        ) : filteredItems.length > 0 ? (
          <div className="fr-cards-list">
            {filteredItems.map((item, idx) => (
              <React.Fragment key={item[0] || idx}>
                {renderCard(item, handleCardClick)}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={emptyIcon}
            title={emptyTitle}
            description={
              hasActiveFilters
                ? 'Try adjusting your search'
                : `No results for ${getFilterTitle()}`
            }
          />
        )}
      </div>

      {selectedItem && renderModal && (
        renderModal(selectedItem, handleModalClose, handleFormSubmit, isSubmitting, setToast, extraData)
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default FilterTemplate;